import {
  Flex,
  Box,
  Input,
  Image,
  Stack,
  Heading,
  Text,
  Link,
  Fieldset,
  HStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthContext } from 'src/auth/AuthContext';
import { authService } from 'src/service/AuthService';
import { userService } from 'src/service/UserService';
import WebUser from 'src/auth/WebUser';
import apiClient from 'src/service/core/ApiClient';
import { Button } from "@/components/ui/button"
import { InputGroup } from "@/components/ui/input-group"
import { Field } from "@/components/ui/field"
import { Avatar } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { PasswordInput } from "@/components/ui/password-input";
import { EmailIcon, LockIcon } from '@/components/icons/Icon';
import { useI18n } from 'src/i18n/I18nContext';
import { FcGoogle } from 'react-icons/fc';
import { FaMicrosoft } from 'react-icons/fa';

interface LoginEntry {
  email: string;
  password: string;
  rememberMe: boolean
}

type OidcProvider = 'google' | 'microsoft';

const OIDC_PROVIDER_STORAGE_KEY = 'peregin_oidc_provider';

const oidcProvider = (value: string | null): OidcProvider | null =>
  value === 'google' || value === 'microsoft' ? value : null;

const oidcProviderLabel = (provider: OidcProvider | null): string => {
  switch (provider) {
    case 'google':
      return 'Google';
    case 'microsoft':
      return 'Microsoft';
    default:
      return 'OIDC';
  }
};

const Login = () => {
  const { t } = useI18n();
  const {
    register,
    control,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginEntry>({
    defaultValues: {
      email: '',
      password: '',
      rememberMe: true
    }
  });

  const { login, logout } = useAuthContext();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [oidcLoadingProvider, setOidcLoadingProvider] = useState<OidcProvider | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oauthToken = searchParams.get('token');
  const oauthError = searchParams.get('error') ?? searchParams.get('oauth');
  const oauthProvider = oidcProvider(searchParams.get('provider') ?? sessionStorage.getItem(OIDC_PROVIDER_STORAGE_KEY));

  useEffect(() => {
    if (oauthError) {
      logout();
      sessionStorage.removeItem(OIDC_PROVIDER_STORAGE_KEY);
      setOidcLoadingProvider(null);
      setError(t('login.oidcError'));
      navigate('/login', { replace: true });
      return;
    }
    if (!oauthToken) {
      return;
    }
    // handle OIDC login
    setOidcLoadingProvider(oauthProvider);
    setError('');
    userService.getMe(oauthToken).then(me => {
      let webUser: WebUser = { token: oauthToken, data: me };
      login(webUser);
      console.info(`${webUser.data.firstName} succesfully logged in with ${oidcProviderLabel(oauthProvider)}...`);
      navigate('/', { replace: true });
    }).catch(error => {
      console.error(error.message);
      logout();
      setError(t('login.oidcError'));
      navigate('/login', { replace: true });
    }).finally(() => {
      sessionStorage.removeItem(OIDC_PROVIDER_STORAGE_KEY);
      setOidcLoadingProvider(null);
    });
  }, [oauthError, oauthProvider, oauthToken, login, logout, navigate, t]);


  const onSubmitLogin: SubmitHandler<LoginEntry> = (entry) => {
    console.info('logging in...');
    setIsLoading(true);
    setError('');
    authService.login(entry.email, entry.password, entry.rememberMe).then(r => userService.getMe(r.token).then(me => {
      let webUser: WebUser = { token: r.token, data: me };
      login(webUser);
      setIsLoading(false);
      console.info(`${webUser.data.firstName} succesfully logged in...`);
      // go to the landing page
      navigate('/');
    })).catch(error => {
      console.error(error.message)
      // in case of errors, logout
      logout();
      if (error.status === 401) {
        setError(t('login.invalidCredentials'));
      } else {
        setError(t('login.connectionError'));
      }
    }).finally(() =>
      setIsLoading(false)
    );
  };

  const onOidcLogin = (provider: OidcProvider) => {
    setError('');
    setOidcLoadingProvider(provider);
    sessionStorage.setItem(OIDC_PROVIDER_STORAGE_KEY, provider);

    const url = new URL(`/api/auth/${provider}/start`, apiClient.baseUrl);
    url.searchParams.set('remember', String(getValues('rememberMe')));
    url.searchParams.set('returnTo', `/login?provider=${provider}`);
    window.location.assign(url.toString());
  };

  return (
    <Flex flex="1" align="center" justify="center" px={{ base: 4, md: 8, xl: 12 }} py={{ base: 6, md: 10 }} bg="gray.50">
      <Flex
        w="full"
        maxW="6xl"
        minH={{ base: 'auto', lg: '650px' }}
        bg="white"
        borderWidth={{ base: '0', lg: '1px' }}
        borderColor="gray.200"
        rounded={{ base: 'none', lg: 'lg' }}
        boxShadow={{ base: 'none', lg: '0 24px 70px rgba(15, 23, 42, 0.10)' }}
        overflow="hidden"
      >
            <Box
              flex="1.08"
              position="relative"
              display={{ base: 'none', lg: 'block' }}
              bg="gray.900"
            >
              <Image
                alt={'Login image'}
                src='/images/org/demo/flower.webp'
                w="full"
                h="full"
                minH="650px"
                objectFit="cover"
              />
              <Box position="absolute" inset="0" bg="linear-gradient(180deg, rgba(17, 24, 39, 0.04) 0%, rgba(17, 24, 39, 0.72) 100%)" />
              <Stack
                position="absolute"
                left="10"
                right="10"
                bottom="10"
                gap="3"
                color="white"
              >
                <Box w="12" h="1" bg="green.300" rounded="full" />
                <Heading fontSize="3xl" lineHeight="1.1" letterSpacing="0">
                  {t('hero.title')}
                </Heading>
                <Text color="whiteAlpha.900" fontSize="lg" maxW="sm">
                  {t('hero.subtitle')}
                </Text>
              </Stack>
            </Box>

            <Flex
              flex="1"
              align="center"
              justify="center"
              px={{ base: 0, sm: 6, md: 10, xl: 14 }}
              py={{ base: 4, md: 10 }}
            >
              <form noValidate onSubmit={handleSubmit(onSubmitLogin)} style={{ width: '100%' }}>
                <Fieldset.Root
                  size={{ base: 'md', md: 'lg' }}
                  invalid={!!error}
                  w="full"
                  maxW="29rem"
                  mx="auto"
                  bg="white"
                  borderWidth={{ base: '1px', lg: '0' }}
                  borderColor="gray.200"
                  rounded="lg"
                  boxShadow={{ base: '0 16px 46px rgba(15, 23, 42, 0.08)', lg: 'none' }}
                  py={{ base: 7, md: 0 }}
                  px={{ base: 5, sm: 8, md: 0 }}
                >
                  <Stack gap={{ base: 7, md: 8 }}>
                    <HStack gap="3" align="center">
                      <Avatar bg="green.400" color="white" size="lg" />
                      <Stack gap="1">
                        <Heading fontSize={{ base: '3xl', md: '4xl' }} lineHeight="1.08" letterSpacing="0">
                          {t('login.title')}
                        </Heading>
                        <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600">
                          {t('login.welcome')}
                        </Text>
                      </Stack>
                    </HStack>

                    <Fieldset.Content gap="5">
                      <Stack gap="4">
                        <Stack direction={{ base: 'column', sm: 'row' }} align="stretch" gap="3">
                          <Button
                            type="button"
                            loading={oidcLoadingProvider === 'microsoft'}
                            loadingText={t('login.microsoftLoading')}
                            onClick={() => onOidcLogin('microsoft')}
                            size="lg"
                            variant="outline"
                            color="gray.700"
                            borderColor="gray.300"
                            flex="1"
                            minW="0"
                            minH="12"
                            h="auto"
                            px={{ base: 3, sm: 4 }}
                            fontSize={{ base: 'xs', sm: 'sm' }}
                            lineHeight="1.2"
                            whiteSpace="nowrap"
                            _hover={{ bg: 'gray.50', borderColor: 'gray.400' }}
                          >
                            <FaMicrosoft size={20} color="#5E5E5E" />
                            <Text as="span" textAlign="center" whiteSpace="nowrap">{t('login.signInWithMicrosoft')}</Text>
                          </Button>
                          <Button
                            type="button"
                            loading={oidcLoadingProvider === 'google'}
                            loadingText={t('login.googleLoading')}
                            onClick={() => onOidcLogin('google')}
                            size="lg"
                            variant="outline"
                            color="gray.700"
                            borderColor="gray.300"
                            flex="1"
                            minW="0"
                            minH="12"
                            h="auto"
                            px={{ base: 3, sm: 4 }}
                            fontSize={{ base: 'xs', sm: 'sm' }}
                            lineHeight="1.2"
                            whiteSpace="nowrap"
                            _hover={{ bg: 'gray.50', borderColor: 'gray.400' }}
                          >
                            <FcGoogle size={22} />
                            <Text as="span" textAlign="center" whiteSpace="nowrap">{t('login.signInWithGoogle')}</Text>
                          </Button>
                        </Stack>
                        <Stack direction="row" align="center" gap="3">
                          <Box flex="1" h="1px" bg="gray.200" />
                          <Text textStyle="sm" color="gray.500">{t('login.or')}</Text>
                          <Box flex="1" h="1px" bg="gray.200" />
                        </Stack>
                      </Stack>

                      <Field id="email" label={t('login.email')} required invalid={!!errors.email} errorText={errors.email?.message}>
                        <InputGroup startElement={<EmailIcon color="gray.400" />} w="full">
                          <Input
                            autoFocus={true}
                            type="email"
                            autoComplete="email"
                            h="12"
                            bg="white"
                            borderColor="gray.300"
                            _focusVisible={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                            {...register("email", { required: t('login.emailRequired') })}
                          />
                        </InputGroup>
                      </Field>
                      <Field id="password" label={t('login.password')} required invalid={!!errors.password} errorText={errors.password?.message}>
                        <InputGroup startElement={<LockIcon color="gray.400" />} w="full">
                          <PasswordInput
                            autoComplete="current-password"
                            h="12"
                            bg="white"
                            borderColor="gray.300"
                            _focusVisible={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                            {...register("password", { required: t('login.passwordRequired') })}
                          />
                        </InputGroup>
                      </Field>

                      <Stack gap="5">
                        <Field id="rememberMe">
                          <Stack
                            direction={{ base: 'column', sm: 'row' }}
                            align={{ base: 'start', sm: 'center' }}
                            justify="space-between"
                            gap="3">
                            <Controller
                              control={control}
                              name="rememberMe"
                              render={({ field: { onChange, value, ref } }) => (
                                <Switch colorPalette="blue" checked={value} onCheckedChange={e => onChange(e.checked)} ref={ref}>{t('login.rememberMe')}</Switch>
                              )}
                            />
                            <Text textStyle="sm">
                              <Link color="blue.600" fontWeight="medium" onClick={_ => navigate('/forgot-password')}>{t('login.forgotPassword')}</Link>
                            </Text>
                          </Stack>
                        </Field>
                        <Button
                          type="submit"
                          loading={isLoading}
                          loadingText={t('login.loading')}
                          size="lg"
                          w="full"
                          bg="blue.500"
                          color="white"
                          _hover={{ bg: 'blue.600' }}
                        >
                          {t('login.submit')}
                        </Button>
                      </Stack>
                    </Fieldset.Content>

                    <Fieldset.ErrorText>
                      {error}
                    </Fieldset.ErrorText>
                  </Stack>
                </Fieldset.Root>
              </form>
            </Flex>
      </Flex>
    </Flex>
  );
}

export default Login
